import Modal from "@/components/Modal";
import PdfViewer from "@/components/PdfViewer";
import SkeletonRow from "@/components/SkeletonRow";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"; // Asumsi menggunakan komponen Table shadcn
import useDataPendaftaran from "@/hooks/useDataPendaftaran";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { getListBerkasPenerimaan } from "@/services/getData";
import { DataBerkas, SysBerkas } from "@/types";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  UploadCloud,
} from "lucide-react";
import { useState } from "react";
import Form from "./Form";

export default function UnggahBerkas({
  idDataPendaftaran,
}: {
  idDataPendaftaran: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });
  const { isPendingPeriode, timePassedActive } = useTimePeriode();
  const { isValidPendaftaran } = useDataPendaftaran();

  const [showUpload, setShowUpload] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SysBerkas | null>(null);
  const [selectedBerkas, setSelectedBerkas] = useState<DataBerkas | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["listDataBerkas", sessionPeriode?.id_sys_periode],
    queryFn: () =>
      getListBerkasPenerimaan(sessionPeriode?.id_sys_periode || ""),
  });

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col gap-1">
        <h1 className="font-bold text-2xl tracking-tight">
          Berkas Persyaratan
        </h1>
        <p className="text-muted-foreground text-sm">
          Kelola dan unggah dokumen persyaratan Anda. Pastikan dokumen terbaca
          jelas.
        </p>
      </div>

      {isPending || isPendingPeriode ? (
        <div className="space-y-4">
          {Array(5)
            .fill(null)
            .map((_, index) => (
              <SkeletonRow key={index} />
            ))}
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                <TableHead className="w-[50px] text-center">No</TableHead>
                <TableHead className="min-w-[250px]">
                  Informasi Dokumen
                </TableHead>
                <TableHead className="w-[150px] text-center">Status</TableHead>
                <TableHead className="w-[100px] text-center">
                  Template
                </TableHead>
                <TableHead className="w-[180px] text-end pr-6">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.listSysBerkas &&
                data?.listSysBerkas?.length > 0 &&
                data.listSysBerkas?.map((row: SysBerkas, index: number) => {
                  const berkasSiswa = data?.listDataBerkasSiswa.find(
                    (berkas: DataBerkas) =>
                      berkas?.id_sys_berkas == String(row.id_sys_berkas),
                  );

                  const isUploaded = !!berkasSiswa;
                  const isLocked =
                    isValidPendaftaran ||
                    timePassedActive?.waktu_selesai_daftar_valid_selesai;

                  return (
                    <TableRow
                      key={row.id_sys_berkas}
                      className="hover:bg-muted/40 transition-colors"
                    >
                      {/* Kolom No */}
                      <TableCell className="text-center font-medium text-muted-foreground">
                        {index + 1}
                      </TableCell>

                      {/* Kolom Info Dokumen (Merged) */}
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-base">
                              {row.jenis_berkas?.nama_jenis_berkas}
                            </span>
                            {row.is_wajib ? (
                              <Badge
                                variant="destructive"
                                className="text-[10px] px-1.5 py-0 h-5"
                              >
                                Wajib
                              </Badge>
                            ) : (
                              ""
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {row?.keterangan_berkas || "-"}
                          </p>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                            <FileText size={12} /> Format:{" "}
                            {row.extension_allowed?.join(", ")}
                          </div>
                        </div>
                      </TableCell>

                      {/* Kolom Status */}
                      <TableCell className="text-center">
                        {isUploaded ? (
                          <Badge
                            variant="outline"
                            className="bg-green-50 text-green-700 border-green-200 gap-1 px-3 py-1"
                          >
                            <CheckCircle2 size={12} /> Uploaded
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="text-muted-foreground gap-1 px-3 py-1"
                          >
                            <AlertCircle size={12} /> Not Yet
                          </Badge>
                        )}
                      </TableCell>

                      {/* Kolom Template */}
                      <TableCell className="text-center">
                        {row.url_template_berkas ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                            title="Unduh Template"
                            onClick={() =>
                              window.open(
                                `${row.url_template_berkas}?id_sys_periode=${sessionPeriode?.id_sys_periode}`,
                                "_blank",
                              )
                            }
                          >
                            <Download size={18} />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>

                      {/* Kolom Aksi */}
                      <TableCell className="text-right pr-6">
                        <div className="flex justify-end items-center gap-2">
                          {/* Tombol Lihat */}
                          {isUploaded && (
                            <Button
                              variant="secondary"
                              size="sm"
                              className="h-8 px-3 text-xs gap-1.5"
                              onClick={() => {
                                setShowPdf(true);
                                setSelectedRow(row);
                                setSelectedBerkas(berkasSiswa);
                              }}
                            >
                              <Eye size={14} /> Lihat
                            </Button>
                          )}

                          {/* Tombol Upload */}
                          {!isLocked && (
                            <Button
                              variant={isUploaded ? "outline" : "default"}
                              size="sm"
                              className={`h-8 px-3 text-xs gap-1.5 ${
                                isUploaded ? "border-dashed text-primary" : ""
                              }`}
                              onClick={() => {
                                setShowUpload(true);
                                setSelectedRow(row);
                              }}
                            >
                              <UploadCloud size={14} />
                              {isUploaded ? "Ganti" : "Upload"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* --- MODALS --- */}

      <Modal show={showUpload} onClose={() => setShowUpload(false)}>
        <Form
          method="POST"
          selectedRow={selectedRow}
          closeModal={() => setShowUpload(false)}
          idDataPendaftaran={idDataPendaftaran}
          selectedBerkas={selectedBerkas}
        />
      </Modal>

      <Modal
        show={showPdf}
        onClose={() => {
          setShowPdf(false);
          // Optional: clear selected setelah delay animasi
          setTimeout(() => setSelectedBerkas(null), 300);
        }}
        className="lg:w-[60vw] w-[95vw]"
      >
        <div className="flex flex-col gap-4 p-2">
          <div>
            <h1 className="font-bold text-lg text-slate-900 dark:text-white">
              {selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas}
            </h1>
            {selectedBerkas?.nomor && (
              <Badge className="mt-2 bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300">
                Nomor: {selectedBerkas?.nomor}
              </Badge>
            )}
          </div>

          {/* Panggil komponen PdfViewer */}
          {showPdf && selectedBerkas?.path_berkas && (
            <PdfViewer url={selectedBerkas.path_berkas} />
          )}
        </div>
      </Modal>
    </div>
  );
}
